import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectEffectiveDate,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSubjectDepreciationRatioForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateMultiFamilyUnitsAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatio: number | undefined,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  compID: string,
) => {
  const subjectImprovements = improvements.subject;
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(depreciationRatio) ||
    isUndefined(effectiveDate)
  ) {
    return undefined;
  }

  const {
    gla: subjGba,
    craftsmanQuality: quality,
    propertyType: subjPropertyType,
  } = subjectImprovements;
  const {
    gla: compGba,
    craftsmanQuality: compQuality,
    propertyType: compPropertyType,
  } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;

  const subjUnits =
    subjPropertyType === 'multiFamily' ? subjectImprovements.numberOfUnits : undefined;
  const compUnits = compPropertyType === 'multiFamily' ? compImprovements.numberOfUnits : undefined;

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  if (
    isUndefined(zipCode) ||
    isUndefined(subjGba) ||
    isUndefined(compGba) ||
    isUndefined(subjUnits) ||
    isUndefined(compUnits) ||
    isUndefined(quality) ||
    isUndefined(compQuality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1
  ) {
    return undefined;
  }

  return adjCalculator.getMultiFamilyUnitsAdj({
    subject: {
      quality,
      avgUnitSqft: AloftArea.schema.parse(subjGba),
      units: subjUnits,
      zipCode,
    },
    comp: {
      avgUnitSqft: AloftArea.schema.parse(compGba),
      units: compUnits,
      quality: compQuality,
      zipCode,
    },
    depreciationRatio,
  }).adjustment;
};

const selectMultiFamilyUnitsAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  calculateMultiFamilyUnitsAdj,
);

export const selectMultiFamilyUnitsAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectSubjectDepreciationRatioForFeature(state, undefined),
    selectEffectiveDate,
    selectCompSequence,
  ],
  (
    adjustmentProps,
    improvements,
    propertyIdentification,
    depreciationRatio,
    effectiveDate,
    compSequence,
  ) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateMultiFamilyUnitsAdj(
        adjustmentProps,
        improvements,
        propertyIdentification,
        depreciationRatio,
        effectiveDate,
        propID,
      );
      if (isDefined(adj)) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedMultiFamilyUnitsAdj = createPriceSelector(
  [selectMultiFamilyUnitsAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'units', adjSettings);
  },
);

export const selectAppliedMultiFamilyUnitsAdjDisplay = createSelector(
  [selectAppliedMultiFamilyUnitsAdj],
  (adj) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);
